import { LogCategory, RenderingStatus, UserRole } from "Enumerations";
import { useCallback, useEffect, useMemo } from "react";

import { AuthError } from "@azure/msal-browser";
import { runInAction } from "mobx";
import { useLogging } from "Shared/Hooks";
import { useMyManagers } from "./useMyManagers";
import { useRootStore } from "Store/RootStore";
import { useUpdateCurrentUserOCERole } from "./useUpdateCurrentUserOCERole";
import { useORPCClient, useORPCQuery } from "Core/oRPC";
import { useQuery } from "@tanstack/react-query";

export const useAfterAuth = () => {
    const myStore = useRootStore("MyStore");
    const logStore = useRootStore("LogStore");
    const authStore = useRootStore("AuthStore");
    const { trackEvent } = useLogging(LogCategory.Auth);
    const { coreApi: coreApiQuery, oncallApi: oncallApiQuery } = useORPCQuery();
    const { coreApi } = useORPCClient();

    useUpdateCurrentUserOCERole();
    useMyManagers();
    const { data: contact } = useQuery(
        oncallApiQuery.getContactv2.queryOptions({
            input: { alias: myStore.profile.Alias ?? "" },
            staleTime: Infinity,
            enabled: !!myStore.profile.Alias
        })
    );

    const teams = useMemo(() => contact?.Teams?.map((team) => (team.PublicId?.split("\\")[0] ?? "") + "\\" + (team.Name ?? "")), [contact?.Teams]);

    const { data: flightings, status } = useQuery(
        coreApiQuery.postFlighting.queryOptions({
            input: {
                Type: "Team",
                Targets: teams
            },
            staleTime: Infinity,
            enabled: !!teams
        })
    );

    useEffect(() => {
        if (status === "success") {
            myStore.flighting = flightings.map((team) => team.toLowerCase());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const roleCheck = useCallback(async () => {
        if (authStore.loginStatus === RenderingStatus.Ready) {
            return;
        }

        const profile = await coreApi.getCurrentUser();
        runInAction(() => (myStore.profile = profile));
        if (!myStore.profile.Alias || !myStore.profile.Upn) {
            authStore.setLoginStatus(RenderingStatus.Error, new AuthError("access_denied", "", "We can't verify your token."));
            return;
        }
        if (!myStore.profile.Roles || !(myStore.profile.Roles.includes(UserRole.Normal) || myStore.profile.Roles.includes(UserRole.Gallatin))) {
            authStore.setLoginStatus(
                RenderingStatus.Error,
                new AuthError("access_denied", "", "The user which is signed in doesn't have access to M365 pulse")
            );
            return;
        }
        authStore.setLoginStatus(RenderingStatus.Ready);
        logStore.setAuthenticatedUserContext(myStore.profile.Alias ? myStore.profile.Alias : "unknown");
        trackEvent("87d24efc-3fe5-470b-bbb9-d6783288a0c7", "Authentication succeeded", {
            LoginType: authStore.LoginType
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStore, logStore, myStore, trackEvent]);
    useEffect(() => {
        if (authStore.readyToCheckRole) {
            roleCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStore.readyToCheckRole]);
};
